import { useCallback, useEffect, useRef, useState } from 'react';
import logger, { LogCategory } from '@/lib/logging';

/**
 * Portable camera-device inventory hook (no component-specific dependencies by design —
 * built for VisioMeter2/VGon but intended for reuse by any live-capture module, e.g.
 * posture-analysis CaptureModule).
 *
 * Owns: the list of video-input devices, a learned availability status per device, and
 * refresh on `devicechange`. Deliberately does NOT own streams — starting/stopping
 * getUserMedia stays with the consumer, which reports outcomes back via setAvailability.
 *
 * Availability semantics:
 * - 'needs-permission' — enumerateDevices returned blank labels (camera permission not
 *   yet granted); selecting such a device should trigger a permission prompt, not fail
 * - 'available'   — a start succeeded on this device this session
 * - 'unavailable' — a start failed on this device this session (in use by another app, etc.)
 * - 'unknown'     — no attempt yet (optimistically selectable)
 */
export type CameraAvailability = 'available' | 'unavailable' | 'needs-permission' | 'unknown';

export interface CameraDeviceOption {
    deviceId: string;
    label: string;
    availability: CameraAvailability;
}

export function useCameraDevices() {
    const [devices, setDevices] = useState<CameraDeviceOption[]>([]);
    const availabilityRef = useRef<Map<string, CameraAvailability>>(new Map());

    const refresh = useCallback(async (): Promise<CameraDeviceOption[]> => {
        if (typeof navigator === 'undefined' || !navigator.mediaDevices?.enumerateDevices) return [];
        try {
            const all = await navigator.mediaDevices.enumerateDevices();
            const cams = all.filter(d => d.kind === 'videoinput');
            const hasLabels = cams.some(d => d.label);
            const next = cams.map((d, i) => ({
                deviceId: d.deviceId,
                label: d.label || `Camera ${i + 1}`,
                availability:
                    availabilityRef.current.get(d.deviceId) ??
                    (hasLabels ? ('unknown' as const) : ('needs-permission' as const)),
            }));
            setDevices(next);
            return next;
        } catch (err) {
            logger.warn('useCameraDevices: enumerateDevices failed', {
                category: LogCategory.CAMERA,
                errorMessage: (err as Error)?.message,
            });
            return [];
        }
    }, []);

    const setAvailability = useCallback((deviceId: string, availability: CameraAvailability) => {
        if (!deviceId) return;
        availabilityRef.current.set(deviceId, availability);
        setDevices(prev => prev.map(d => (d.deviceId === deviceId ? { ...d, availability } : d)));
    }, []);

    useEffect(() => {
        const onDeviceChange = () => {
            void refresh();
        };
        navigator.mediaDevices?.addEventListener?.('devicechange', onDeviceChange);
        return () => navigator.mediaDevices?.removeEventListener?.('devicechange', onDeviceChange);
    }, [refresh]);

    return { devices, refresh, setAvailability };
}
